/**
 * Terrain catalogue for After the Fall. Gameplay meaning lives here; visuals
 * live in render/. Keys are stable identifiers (used by data, saves and tests);
 * names and descriptions are player-facing.
 *
 * `walkable`  — units may path through it (pathfinding arrives with units).
 * `buildable` — buildings may be placed on it (enforced by Phase 2 placement).
 */
export const TerrainType = {
  Grass: 0,
  Meadow: 1,
  Dirt: 2,
  Forest: 3,
  Rock: 4,
  Water: 5,
  Road: 6,
  Ruins: 7,
} as const;

export type TerrainType = (typeof TerrainType)[keyof typeof TerrainType];

export interface TerrainInfo {
  readonly id: TerrainType;
  readonly key: string;
  readonly name: string;
  readonly walkable: boolean;
  readonly buildable: boolean;
  readonly description: string;
}

const TERRAIN_LIST: readonly TerrainInfo[] = [
  { id: TerrainType.Grass, key: 'grass', name: 'Overgrown Grass', walkable: true, buildable: true, description: 'Untended grassland, reclaimed by weeds. Solid ground for building.' },
  { id: TerrainType.Meadow, key: 'meadow', name: 'Abandoned Field', walkable: true, buildable: true, description: 'Former farmland gone wild. Buildable; good soil for farms.' },
  { id: TerrainType.Dirt, key: 'dirt', name: 'Wasteland', walkable: true, buildable: true, description: 'Dry, packed earth. Buildable.' },
  { id: TerrainType.Forest, key: 'forest', name: 'Forest', walkable: true, buildable: false, description: 'Dense woodland. Passable on foot but cannot be built on. Timber for sawmills.' },
  { id: TerrainType.Rock, key: 'rock', name: 'Rock Outcrop', walkable: false, buildable: false, description: 'Exposed bedrock. Impassable. Future stone source.' },
  { id: TerrainType.Water, key: 'water', name: 'Murky Water', walkable: false, buildable: false, description: 'Stagnant, polluted water. Impassable.' },
  { id: TerrainType.Road, key: 'road', name: 'Cracked Highway', walkable: true, buildable: true, description: 'A broken highway leading out of the colony. Can be built on — including walls to seal it off.' },
  { id: TerrainType.Ruins, key: 'ruins', name: 'Ruins', walkable: false, buildable: false, description: 'Collapsed buildings and rubble from before the fall. Impassable until cleared (later phase).' },
];

const BY_ID = new Map<TerrainType, TerrainInfo>(TERRAIN_LIST.map((t) => [t.id, t]));

export function terrainInfo(id: TerrainType): TerrainInfo {
  const info = BY_ID.get(id);
  if (!info) throw new Error(`Unknown terrain id ${id}`);
  return info;
}

export const ALL_TERRAIN: readonly TerrainInfo[] = TERRAIN_LIST;

/**
 * Passability class used by overlays and (later) placement and pathfinding.
 *   buildable — walkable and buildable
 *   walkable  — walkable only (forest, road)
 *   blocked   — neither (rock, water)
 */
export type Passability = 'buildable' | 'walkable' | 'blocked';

export function passabilityOf(t: TerrainInfo): Passability {
  if (!t.walkable) return 'blocked';
  return t.buildable ? 'buildable' : 'walkable';
}
